import { Link } from 'react-router-dom';
import { hasAnyPermission, isOrgManager, isSuperAdmin } from '@/utils/permissions';

interface RequirePermissionProps {
  anyOf?: string[];
  /** When true, only super admins may view — granular permissions are ignored. */
  superAdmin?: boolean;
  /** When true, super admins plus org admins and PICs may view. */
  orgManager?: boolean;
  children: React.ReactNode;
}

export default function RequirePermission({
  anyOf = [],
  superAdmin = false,
  orgManager = false,
  children,
}: RequirePermissionProps) {
  const allowed = superAdmin
    ? isSuperAdmin()
    : orgManager
      ? isOrgManager()
      : hasAnyPermission(anyOf);
  if (!allowed) {
    return <AccessDenied />;
  }
  return <>{children}</>;
}

function AccessDenied() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-500">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-7 w-7">
          <path
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v4m0 4h.01M10.29 3.86l-8.18 14.18A2 2 0 004 21h16a2 2 0 001.89-2.96L13.71 3.86a2 2 0 00-3.42 0z"
          />
        </svg>
      </div>
      <h2 className="text-lg font-bold text-stone-800">Access denied</h2>
      <p className="max-w-sm text-sm text-stone-500">
        Your account doesn&apos;t have permission to view this page. Contact an administrator if
        you think this is a mistake.
      </p>
      <Link
        to="/dashboard"
        className="mt-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-orange-200 transition hover:bg-orange-600"
      >
        Back to dashboard
      </Link>
    </div>
  );
}
